import { Component, OnInit, Renderer2, HostListener } from '@angular/core';
import { ThemeService } from '../service/theme.service';
import { CognitoService } from '../service/cognito.service';
import { RoleService } from '../service/role.service';
import { environment } from '../environments/environment';
import { FilterService } from '../service/filter.service';
import { Roles } from '../constants/roles';
import { ValidationService } from '../service/validation.service';
import { LocalizationService } from '../service/localization.service';
import { SystemMessageService } from '../service/system-message.service';
import { lastValueFrom } from 'rxjs';
import { Types } from '../constants/user-type';


@Component({
    selector: 'app-dispatch-user-list',
    templateUrl: './dispatch-user-list.component.html',
    styleUrls: ['./dispatch-user-list.component.css', '../../global-elements.css'],
    standalone: false
})
export class DispatchUserListComponent implements  OnInit {

  public currentOperatorId: string = '';
  public currentUserRoles: any;
  public dispatchUserArray: any[] = [];
  public originalDispatchUserArray: any[] = [];
  public userFilter: string = "";
  public filterBy: string = '';
  public ascDesc: number = 0;
  public dispatchUserFilterOptions = [
    { label: 'givenName', value: 'given_name' },
    { label: 'familyName', value: 'family_name' },
    { label: 'email', value: 'email' },
    { label: 'username', value: 'username' },
  ];
  public showEditModal = false;
  public modalMode: 'create' | 'edit' = 'edit';
  public editLoading = false;
  public selectedUserSub = '';
  public Roles = Roles.filter((role) => role.label === '_admin');
  private userRoles: any[] = [];
  private userRolesNumber: any;
  private validateRolesCheckBox = false;

  constructor(
    public theme: ThemeService,
    public cognitoService: CognitoService,
    public roleService: RoleService,
    private renderer: Renderer2,
    private filter: FilterService,
    public validationService: ValidationService,
    public localizationService: LocalizationService,
    public systemMessageService: SystemMessageService
    ){}

  async ngOnInit() {
    await this.cognitoService.confirmValidUser();
    await this.cognitoService.getUserType();

    // Call role service to get the current user roles
    this.currentUserRoles = await this.roleService.getRoles();

    // Restrict to operator user type with dispatch role.
    await this.cognitoService.getCurrentRole(
      [environment.users.role.dispatch],
      [environment.users.maintenance],
      true,
      this.roleService.roles
    );

    // Call the function to initialize all data of the page
    this.initData();
  }

  // Keep phone input formatting consistent with user-update-operator.
  @HostListener('input', ['$event.target']) onInput(input: HTMLInputElement): void {
    if (input.classList.contains('telefone-input')) {
      const value = input.value.replace(/\D/g, '');
      if (value.length <= 12) {
        input.value = this.validationService.formatPhoneNumber(value);
      } else {
        input.value = input.value.slice(0, 12);
      }
    }
  }

  // Asynchronously initialize data
  async initData() {
    // Get the current user using Cognito service
    const currentUser = await this.cognitoService.getUser();

    // Extract and set the current operator ID from user attributes
    this.currentOperatorId =
      currentUser.attributes['custom:operations_id'] ||
      currentUser.attributes['custom:operator_id'] ||
      '';

    // Retrieve all users for the current operator ID
    await this.getAllOperatorUsers(this.currentOperatorId);
  }

  // Load users scoped to the current operator id.
  async getAllOperatorUsers(operatorId: string) {
    const users = await this.cognitoService.getAllUsers();
    const filtered = (users || []).filter(
      (user: any) =>
        user.custom_operator_id === operatorId &&
        user.custom_user_type === environment.users.maintenance
    );
    const mapped = filtered.map((user: any) => ({
      user: user.sub,
      username: user.username,
      given_name: user.given_name,
      family_name: user.family_name,
      address: user.address,
      phone_number: user.phone_number,
      email: user.email,
      current_role: user.custom_current_role || user.custom_role,
      operator_id: user.custom_operator_id,
      user_type: user.custom_user_type,
    }));
    this.dispatchUserArray = mapped;
    this.originalDispatchUserArray = mapped;
  }

  // Function used to made a search in the list
  search(){
    // Check if there's a filterBy variable and set it to it's default value
    if(this.filterBy === '' || !this.filterBy){
      this.filterBy = 'given_name';
    }

    this.dispatchUserArray = this.filter.search(
      this.userFilter,
      this.dispatchUserArray,
      this.originalDispatchUserArray,
      this.filterBy
    );
    this.onSortChange('select');
  }

  // Function called to get the style of the columns of the grid list
  getColumnStyle(): any {
    const numberOfColumn = this.ajustNumberOfColumns(this.getScreenWidth());
    if(numberOfColumn){
      if(this.dispatchUserArray.length >= numberOfColumn){
        const gridColumns = `repeat(${numberOfColumn}, minmax(0, 300px))`;
        return {
          'grid-template-columns': gridColumns
        };
      }else{
        return {
          'display': `flex`,
          'justify-content': `center`,
          'margin': '0 -10px'
        };
      }
    }
  }

  // function called to adjust the number of column of the grid depbending of inner window width
  ajustNumberOfColumns(width: number): number | undefined{
    // Return the floor of the inner page width divided by 450 px as the width of the tile size
    return Math.floor((width / 325) * (90 / 100));
  }

  // Function to get the current screen width
  getScreenWidth(): number {
    // Return the inner width of the window as the screen width
    return window.innerWidth;
  }

  // Handles changes in sorting for a list of clients.
  onSortChange(caller: string){
    // Switch the value of ascending or descending
    if(caller === 'switch'){
      if(this.ascDesc === 0){
        this.ascDesc = 1;
      }else{
        this.ascDesc = 0;
      }
    }

    const sortField = this.filterBy || 'given_name';
    // Function that sort the list
    this.dispatchUserArray.sort((a, b) => {
      const aValue = (a?.[sortField] ?? '').toString().toLowerCase();
      const bValue = (b?.[sortField] ?? '').toString().toLowerCase();
      if (this.ascDesc === 1) {
        return bValue.localeCompare(aValue);
      }
      return aValue.localeCompare(bValue);
    });
  }

   /**
 * Function that toggles the display of the complete address of the user .
 * @param i the id of the address container
 */
  displayAddress(i:number){
    //get the address container
    let element = document.getElementById(i.toString());
    //logic to display the address
    if(element && element.classList.contains('address')){
      //to show complete address
      this.renderer.removeClass(element,'address')
      this.renderer.addClass(element,'addressVisible')
    } else{
      //to show address with ellipsis
      this.renderer.addClass(element,'address')
      this.renderer.removeClass(element,'addressVisible')
    }
  }

  sanitizeDisplay(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    const raw = String(value);
    return raw
      .replace(/[<>]/g, '')
      .replace(/\\'/g, "'")
      .replace(/\\"/g, '"')
      .replace(/\//g, ' ');
  }

  openCreateModal() {
    if (!this.currentUserRoles?.includes(environment.users.role.administrator)) {
      return;
    }

    this.modalMode = 'create';
    this.showEditModal = true;
    this.editLoading = false;
    this.selectedUserSub = '';
    this.validateRolesCheckBox = false;
    this.roleService.userRoles = [{
      user: 'new-dispatch-user',
      role: 6,
      label: environment.users.role.dispatch,
    }];
    this.userRoles = [...this.roleService.userRoles];
    this.cognitoService.roleUserArray = [environment.users.role.dispatch];
    this.validationService.validationErrorArray = [];
    this.systemMessageService.buttonBlocked = false;
    this.localizationService.autoCompletionReturnAddress = '';
    this.cognitoService.clearCognitoUserData();
    this.cognitoService.cognitoUserData.custom_operator_id = this.currentOperatorId;
    this.cognitoService.cognitoUserData.custom_user_type = Types[3].label;
    this.cognitoService.cognitoUserData.custom_current_role = environment.users.role.dispatch;
    this.localizationService.initAutocomplete();
  }

  // Navigate to the user update page with specified parameters
  async openEditModal(userSub: string) {
    if (!this.currentUserRoles?.includes(environment.users.role.administrator)) {
      return;
    }
    this.modalMode = 'edit';
    this.showEditModal = true;
    this.editLoading = true;
    this.selectedUserSub = userSub;
    this.validateRolesCheckBox = false;
    this.roleService.userRoles = [];
    this.userRoles = [];
    this.validationService.validationErrorArray = [];
    this.localizationService.autoCompletionReturnAddress = '';
    this.cognitoService.clearCognitoUserData();

    try {
      const res: any = await lastValueFrom(this.cognitoService.getUserData(userSub));
      const user = Array.isArray(res) ? res[0] : null;
      if (user) {
        this.populateCognitoUserData(user);
      }

      const response = await lastValueFrom(await this.roleService.getRolesForUser(userSub));
      this.userRolesNumber = response;
      if (this.userRolesNumber && Array.isArray(this.userRolesNumber)) {
        this.userRoles = this.userRolesNumber.map((userRole: any) => {
          const roleDefinition = Roles.find((role: any) => role.value === userRole.role);
          return roleDefinition
            ? { user: userSub, role: Number(roleDefinition.value), label: roleDefinition.label }
            : null;
        }).filter((role) => role !== null);
      }
      this.roleService.userRoles = this.userRoles;
      this.updateUserRoles();
      this.localizationService.initAutocomplete();
    } catch (error) {
      console.error('Failed to load user for edit', error);
    } finally {
      this.editLoading = false;
    }
  }

  closeEditModal() {
    this.showEditModal = false;
    this.modalMode = 'edit';
    this.selectedUserSub = '';
    this.editLoading = false;
    this.validateRolesCheckBox = false;
    this.roleService.userRoles = [];
    this.userRoles = [];
    this.cognitoService.roleUserArray = [];
    this.validationService.validationErrorArray = [];
    this.localizationService.autoCompletionReturnAddress = '';
    this.systemMessageService.buttonBlocked = false;
    this.cognitoService.clearCognitoUserData();
  }

  populateCognitoUserData(user: any) {
    this.cognitoService.cognitoUserData.username = user.username;
    this.cognitoService.cognitoUserData.given_name = user.given_name;
    this.cognitoService.cognitoUserData.family_name = user.family_name;
    this.cognitoService.cognitoUserData.email = user.email;
    this.cognitoService.cognitoUserData.phone_number = this.validationService.formatPhoneNumber(user.phone_number);
    this.cognitoService.cognitoUserData.address = user.address;
    this.cognitoService.cognitoUserData.custom_current_role = user.current_role;
    this.cognitoService.cognitoUserData.sub = user.user;
    this.cognitoService.cognitoUserData.enabled = user.enable;
    this.cognitoService.cognitoUserData.custom_user_type = user.user_type;
    this.cognitoService.cognitoUserData.custom_client_id = user.client_id || '';
    this.cognitoService.cognitoUserData.custom_distributor_id = user.distributor_id || '';
    this.cognitoService.cognitoUserData.custom_operator_id = user.operator_id || '';
    this.cognitoService.cognitoUserData.client_name = user.client_name || '';
  }

  updateUserRoles(): void {
    this.userRoles = this.userRoles.map(this.roleService.addLabel);
  }

  ensureDispatchRolePresent(userIdentifier: string): void {
    const hasDispatchRole = this.userRoles.some((role: { label?: string }) => role.label === environment.users.role.dispatch);
    if (!hasDispatchRole) {
      this.userRoles.push({
        user: userIdentifier,
        role: 6,
        label: environment.users.role.dispatch,
      });
    }
    this.roleService.userRoles = this.userRoles;
  }

  isRoleChecked(roleLabel: string): boolean {
    return this.userRoles && this.userRoles.some((role: { label?: string }) => role.label === roleLabel);
  }

  onInputAddressKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }

  toggleRole(roleLabel: string) {
    if (this.modalMode === 'create' && roleLabel === environment.users.role.dispatch) {
      return;
    }

    const userIdentifier = this.modalMode === 'create'
      ? 'new-dispatch-user'
      : this.cognitoService.cognitoUserData.sub;

    if (!userIdentifier) {
      return;
    }
    this.roleService.toggleRoleUpdate(roleLabel, userIdentifier);
    this.userRoles = this.roleService.userRoles;
  }

  async saveUser() {
    if (this.modalMode === 'create') {
      await this.createUser();
      return;
    }

    this.ensureDispatchRolePresent(this.cognitoService.cognitoUserData.sub);

    if (this.userRoles && this.userRoles.length > 0) {
      this.validateRolesCheckBox = true;
    }
    if (this.localizationService.autoCompletionReturnAddress !== '') {
      this.cognitoService.cognitoUserData.address = this.localizationService.autoCompletionReturnAddress;
    }

    this.validationService.checkValidationAllInputs();
    if (
      !this.validationService.givenNameValid ||
      !this.validationService.familyNameValid ||
      !this.validationService.clientPhoneNumberValid ||
      !this.validationService.clientAddressValid ||
      !this.validateRolesCheckBox
    ) {
      if (!this.validateRolesCheckBox) {
        this.systemMessageService.selectRibbon('danger', 'leastOneRoleSelected');
      } else {
        this.systemMessageService.selectRibbon('danger', 'fieldEmptyOrIncorrect');
      }
      return;
    }

    const updateUserResult = await this.cognitoService.updateUserAttributesCognito(
      this.cognitoService.cognitoUserData.username
    );
    if (updateUserResult) {
      try {
        await this.roleService.deleteAllUserRole(this.userRoles[0].user);
        await this.roleService.updateUserRole(this.userRoles[0].user, this.userRoles);
        this.systemMessageService.selectRibbon('success', 'alert-success-generic-message');
        this.closeEditModal();
        await this.getAllOperatorUsers(this.currentOperatorId);
      } catch (error) {
        console.error('Error updating user role:', error);
      }
    }
  }

  async createUser() {
    this.ensureDispatchRolePresent('new-dispatch-user');
    this.validateRolesCheckBox = this.userRoles.length > 0;

    if (this.localizationService.autoCompletionReturnAddress !== '') {
      this.cognitoService.cognitoUserData.address = this.localizationService.autoCompletionReturnAddress;
    }

    this.cognitoService.cognitoUserData.custom_operator_id = this.currentOperatorId;
    this.cognitoService.cognitoUserData.custom_user_type = Types[3].label;
    this.cognitoService.roleUserArray = this.userRoles.map((role: any) => role.label);
    this.cognitoService.cognitoUserData.custom_current_role = this.userRoles.some((role: any) => role.label === environment.users.role.administrator)
      ? environment.users.role.administrator
      : environment.users.role.dispatch;

    this.validationService.keepOnlyNumbers(this.cognitoService.cognitoUserData.phone_number);
    this.cognitoService.cognitoUserData.phone_number = this.validationService.phoneNumberFixed;

    this.validationService.checkValidationAllInputs();
    this.validationService.validateAddress(this.cognitoService.cognitoUserData.address);
    this.validationService.validateRoles();

    if (
      !this.validationService.usernameValid ||
      !this.validationService.givenNameValid ||
      !this.validationService.familyNameValid ||
      !this.validationService.clientEmailValid ||
      !this.validationService.clientPhoneNumberValid ||
      !this.validationService.clientAddressValid ||
      !this.validationService.userRolesValid ||
      !this.validateRolesCheckBox
    ) {
      this.systemMessageService.selectRibbon('danger', !this.validateRolesCheckBox ? 'leastOneRoleSelected' : 'fieldEmptyOrIncorrect');
      return;
    }

    this.systemMessageService.buttonBlocked = true;

    try {
      const result = await this.cognitoService.UserSignUpAndSendTemporaryPassword(
        Types[3].label,
        environment.users.role.dispatch
      );

      if (result === 'duplicated') {
        this.systemMessageService.selectRibbon('danger', 'Duplicated email');
        return;
      }

      this.closeEditModal();
      await this.getAllOperatorUsers(this.currentOperatorId);
    } catch (error: any) {
      let systemMessage = 'fieldEmptyOrIncorrect';

      if (error?.response?.status === 400 && error?.name === 'UsernameExistsException') {
        systemMessage = 'userAlreadyExist';
      } else if (error?.toString?.().includes('UsernameExistsException')) {
        systemMessage = 'userAlreadyExist';
      } else if (error?.toString?.().includes('InvalidParameterException') && error?.toString?.().includes('username')) {
        systemMessage = 'invalidUsernameFormat';
      }

      console.error('Error creating dispatch user:', error);
      this.systemMessageService.selectRibbon('danger', systemMessage);
    } finally {
      this.systemMessageService.buttonBlocked = false;
    }
  }

}
